import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2, AlertCircle, Check, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import logo from "@/assets/logo.png";

type ModalState = "hidden" | "confirm" | "loading" | "success" | "duplicate" | "error";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [modalState, setModalState] = useState<ModalState>("hidden");
  const [pendingEmail, setPendingEmail] = useState("");

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^@]+@[^@]+\.[^@]+$/.test(trimmed)) return;
    setPendingEmail(trimmed);
    setModalState("confirm");
  };

  const handleConfirm = useCallback(async () => {
    setModalState("loading");
    const { error } = await (supabase as any).
    from("newsletter_subscribers").
    insert({ email: pendingEmail, site_key: "xentic" });

    if (error) {
      if (error.code === "23505") {
        setModalState("duplicate");
      } else {
        setModalState("error");
      }
      setTimeout(() => setModalState("hidden"), 4000);
    } else {
      setModalState("success");
      setEmail("");
      setTimeout(() => setModalState("hidden"), 3500);
    }
  }, [pendingEmail]);

  const handleCancel = () => setModalState("hidden");

  return (
    <footer className="section-alt border-t border-border">
      <div className="container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <img src={logo} alt="Xentic Solutions" className="h-[100px] md:h-[120px] w-auto mb-4 invert brightness-200 hue-rotate-180 drop-shadow-[0_0_10px_rgba(255,255,255,0.2)]" />
            <p className="text-sm leading-relaxed text-muted-foreground">AI-powered automation for small businesses. More leads, less effort, zero risk.</p>
            <p className="text-sm mt-4 text-muted-foreground/50">Proudly Backed by Skipper Ventures</p>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-foreground mb-4">Services</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services" className="text-muted-foreground hover:text-primary transition-colors">AI Chatbot Automation</Link></li>
              <li><Link to="/services" className="text-muted-foreground hover:text-primary transition-colors">Website Development</Link></li>
              <li><Link to="/services" className="text-muted-foreground hover:text-primary transition-colors">SEO Optimization</Link></li>
              <li><Link to="/services" className="text-muted-foreground hover:text-primary transition-colors">AI Workflow Automation</Link></li>
              <li><Link to="/services" className="text-muted-foreground hover:text-primary transition-colors">Lead Generation Systems</Link></li>
              <li><Link to="/services" className="text-muted-foreground hover:text-primary transition-colors">Social Media Management</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-foreground mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">About</Link></li>
              <li><Link to="/results" className="text-muted-foreground hover:text-primary transition-colors">Results</Link></li>
              <li><Link to="/pricing" className="text-muted-foreground hover:text-primary transition-colors">Pricing</Link></li>
              <li><Link to="/portfolio" className="text-muted-foreground hover:text-primary transition-colors">Portfolio</Link></li>
              <li><Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">Blog</Link></li>
              <li><Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">Contact</Link></li>
              <li>
                <a
                  href="https://video-editing.xenticsolutions.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Video Editing Portfolio
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-foreground mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="tel:+18722970607" className="flex items-center gap-2.5 text-muted-foreground hover:text-primary transition-colors group">
                  <span className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current text-primary"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg>
                  </span>
                  +1 (872) 297-0607
                </a>
              </li>
              <li>
                <a href="mailto:info@xenticsolutions.com" className="flex items-center gap-2.5 text-muted-foreground hover:text-primary transition-colors group">
                  <span className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current text-primary"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg>
                  </span>
                  info@xenticsolutions.com
                </a>
              </li>
              <li className="flex items-center gap-2.5 text-muted-foreground">
                <span className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-red-400"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>
                </span>
                107 Williamsburg Lane, Bolingbrook, IL 60440
              </li>
            </ul>
          </div>
        </div>

        {/* Modern Map Section */}
        <div className="mt-12 relative">
          <div className="flex flex-col md:flex-row gap-6 items-stretch">
            <div className="md:w-1/3 glass-card rounded-2xl p-6 flex flex-col justify-center">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center mb-4">
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current text-primary"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>
              </div>
              <h4 className="font-heading font-bold text-foreground text-lg mb-1">Our Location</h4>
              <p className="text-sm text-muted-foreground mb-4">107 Williamsburg Lane, Bolingbrook, IL 60440</p>
              <a

                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 text-white text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-green-600 transition-colors w-fit" href="https://wa.me/19178070944">
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                Chat on WhatsApp
              </a>
            </div>
            <div className="md:w-2/3 rounded-2xl overflow-hidden border border-border h-[220px] md:h-[250px] relative">
              <iframe
                title="Xentic Solutions Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2974.5!2d-88.0716!3d41.6986!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880e57a0e5c1e0d5%3A0x0!2s107+Williamsburg+Ln%2C+Bolingbrook%2C+IL+60440!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(0.3) contrast(1.1)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade" />
              <div className="absolute bottom-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-xl text-xs font-bold items-center gap-1.5 shadow-xl animate-bounce z-10 flex flex-col">
                <span>📍</span> We Are Here!
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div id="newsletter" className="mt-12 glass-card rounded-2xl p-8 md:p-10">
          <div className="max-w-2xl mx-auto text-center">
            <h4 className="font-heading font-bold text-foreground text-xl md:text-2xl mb-2">Stay in the Loop</h4>
            <p className="text-sm text-muted-foreground mb-6">Get AI automation tips, updates, and exclusive insights delivered to your inbox.</p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <label htmlFor="newsletter-email" className="sr-only">Email address</label>
              <input
                id="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                aria-label="Email address"
                required
                className="flex-1 h-1 md:h-11 rounded-xl border border-border bg-background/50 px-4 text-base md:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />

              <button
                type="submit"
                className="h-11 px-6 rounded-xl text-sm font-semibold text-primary-foreground border-0 hover:-translate-y-0.5 transition-transform duration-300 shrink-0"
                style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}>

                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Confirmation Modal */}
        <AnimatePresence>
          {modalState !== "hidden" &&
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}>

              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={modalState === "confirm" ? handleCancel : undefined} />
              <motion.div
              className="relative w-full max-w-md rounded-2xl border border-border bg-background p-8 shadow-2xl"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}>

                {modalState === "confirm" &&
              <div className="text-center space-y-5">
                    <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center" style={{ background: "linear-gradient(135deg, hsl(189 100% 50% / 0.2), hsl(243 76% 59% / 0.2))" }}>
                      <CheckCircle2 className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="text-lg font-heading font-bold text-foreground">Confirm Subscription</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      By subscribing, I agree to receive promotional and marketing communications from Xentic Solutions. I understand that I can unsubscribe at any time.
                    </p>
                    <div className="flex gap-3 pt-2">
                      <button
                    onClick={handleCancel}
                    className="flex-1 h-11 rounded-xl border border-border bg-muted text-sm font-medium text-foreground flex items-center justify-center gap-2 hover:bg-muted/80 transition-colors">

                        <X className="h-4 w-4" /> Cancel
                      </button>
                      <button
                    onClick={handleConfirm}
                    className="flex-1 h-11 rounded-xl text-sm font-semibold text-primary-foreground flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                    style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}>

                        <Check className="h-4 w-4" /> Confirm
                      </button>
                    </div>
                  </div>
              }

                {modalState === "loading" &&
              <div className="text-center py-6">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                    <p className="text-sm text-muted-foreground mt-3">Subscribing...</p>
                  </div>
              }

                {modalState === "success" &&
              <div className="text-center py-6 space-y-3">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300 }}>
                      <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto" />
                    </motion.div>
                    <h3 className="text-lg font-heading font-bold text-foreground">Successfully Subscribed ✅</h3>
                    <p className="text-sm text-muted-foreground">You're all set! Watch your inbox.</p>
                  </div>
              }

                {modalState === "duplicate" &&
              <div className="text-center py-6 space-y-3">
                    <AlertCircle className="h-12 w-12 text-yellow-400 mx-auto" />
                    <h3 className="text-lg font-heading font-bold text-foreground">Already Subscribed</h3>
                    <p className="text-sm text-muted-foreground">This email is already on our list!</p>
                  </div>
              }

                {modalState === "error" &&
              <div className="text-center py-6 space-y-3">
                    <AlertCircle className="h-12 w-12 text-red-400 mx-auto" />
                    <h3 className="text-lg font-heading font-bold text-foreground">Something Went Wrong</h3>
                    <p className="text-sm text-muted-foreground">Please try again later.</p>
                  </div>
              }
              </motion.div>
            </motion.div>
          }
        </AnimatePresence>

        <div className="border-t border-border mt-10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Xentic Solutions. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <span className="text-border">|</span>
            <Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>);

};

export default Footer;